import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import { ElMessage } from 'element-plus'

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;
  public static err_count = 0;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_BASE_API;
    ApiService.setHeader()
    ApiService.vueInstance.axios.interceptors.response.use(
      /**
      * 下面的注释为通过在response里，自定义code来标示请求状态
      * 当code返回如下情况则说明权限有问题，登出并返回到登录页
      * 如想通过 xmlhttprequest 来状态码标识 逻辑可写在下面error中
      * 以下代码均为样例，请结合自生需求加以修改，若不需要，则可删除
      */
      response => {
        ApiService.err_count = 0;
        return Promise.resolve(response)
      },
      error => {
        console.log('err:' + error) // for debug
        let err_msg = ''
        if (error.response) {
          // console.log(error.response)
          let err_data = error.response.data;
          if (error.response.status === 401 && JwtService.getRefresh() && err_data.messages
            && ( err_data.messages[0]['message'] == "Token is invalid or expired" || err_data.code == "token_not_valid" )
            && !( error.request.responseURL.includes('/login') || error.request.responseURL.includes('/refresh') )
          ) {
            ApiService.vueInstance.axios
            .post('refresh', {refresh: JwtService.getRefresh()})
            .then(({ data }) => {
              JwtService.saveToken(data.access)
              error.config['Authorization'] = `Token ${data.access}`
              ApiService.vueInstance.axios.create(error.config)
              location.reload()
            })
            .catch(() => {
              JwtService.destroyToken();
              JwtService.destroyRefresh();
              location.href="/#sign-in";
              location.reload();
            });
            // return Promise.reject(error)
          } else if (error.response.status == 400) {
            if (error.response.data['detail'] != undefined) {
              err_msg = error.response.data.detail;
            } else {
              for (let e in error.response.data) {
                err_msg = error.response.data[e][0];
              }
            }
          } else if ([401, 403, 500].indexOf(error.response.status) > -1) {
            if (!error.request.responseURL.includes('/login')) {
              console.log(error.response);
              if (error.response.data.detail) {
                err_msg = error.response.data.detail;
                if ((error.response.data.code && error.response.data.code === "token_not_valid") || (error.response.code && error.response.code === "token_not_valid")) {
                  err_msg = "令牌已过期，请重新登录";
                  JwtService.destroyToken();
                  JwtService.destroyRefresh();
                  location.href = "/#sign-in";
                  location.reload();
                }
              } else if (error.response.data) {
                if (typeof error.response.data === "object") {
                  for (let e in error.response.data) {
                    err_msg = error.response.data[e];
                  }
                } else {
                  err_msg = '服务器错误！';
                }
              } else {
                err_msg = '服务器错误！';
              }
            }
          }
        } else if (error.request) {
          if (error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1) {
            err_msg = '请求超时'
          } else {
            err_msg = '服务器无响应'
          }
          JwtService.destroyToken();
          JwtService.destroyRefresh();
          location.href = "/#sign-in";
          location.reload();
        }
        if (err_msg != '') {
          ApiService.err_count += 1;
          if (ApiService.err_count == 1) {
            ElMessage({
              type: 'error',
              message: err_msg,
              duration: 3000,
              onClose: function () {
                ApiService.err_count = 0;
              }
            })
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common["Authorization"] = `Token ${JwtService.getToken()}`;
  }

  /**
   * @description get the default HTTP request headers
   */
  public static getHeader() {
    return {
      "Authorization": `Token ${JwtService.getToken()}`
    }
  }

  public static getUri(uri) {
    return ApiService.vueInstance.axios.defaults.baseURL + "/" + uri.replace(/^\/+/, "").replace(/\/+$/, "") + (uri.indexOf("?") > -1 ? "" : "/");
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    params = {}
  ): Promise<AxiosResponse> {
    resource += resource.indexOf("?") > -1 ? "" : "/";
    return ApiService.vueInstance.axios.get(`${resource}`, {params}).catch((error) => {
      throw new Error(`[KT] ApiService ${error}`);
    });
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    slug = "" as string
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios
      .get(`${resource}/${slug}`)
      .catch((error) => {
        throw new Error(`[KT] ApiService ${error}`);
      });
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: {}
  ): Promise<AxiosResponse> {
    resource += resource.indexOf("?") > -1 ? "" : "/";
    return ApiService.vueInstance.axios.post(`${resource}`, params as AxiosRequestConfig);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}/`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: {}
  ): Promise<AxiosResponse> {
    resource += resource.indexOf("?") > -1 ? "" : "/";
    return ApiService.vueInstance.axios.put(`${resource}`, params as AxiosRequestConfig);
  }

  /**
   * @description Send the PATCH HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static patch(
    resource: string,
    params: {}
  ): Promise<AxiosResponse> {
    resource += resource.indexOf("?") > -1 ? "" : "/";
    return ApiService.vueInstance.axios.patch(`${resource}`, params as AxiosRequestConfig);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    resource += resource.indexOf("?") > -1 ? "" : "/";
    return ApiService.vueInstance.axios.delete(`${resource}`).catch((error) => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  }
}

export default ApiService;
